/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Central Operations Navigation Header & Session Controller
 * PATH: src/layout/AppHeader.js
 * COMPLIANCE: ISO 27001 Session Management & RBI Security Guidelines
 */

import React, { useState, useEffect } from "react";
import {
  Layout,
  Dropdown,
  Avatar,
  Space,
  Modal,
  Typography,
  message,
} from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  ThunderboltFilled,
  DownOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { logoutAction as logout } from "redux/actions/auth";
import "./AppHeader.css";

const { Header } = Layout;
const { Text } = Typography;
const { confirm } = Modal;

const AppHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Live Digital Clock for Audit Operations
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Secure Administrative Session Logout
  const handleLogout = () => {
    confirm({
      title: "Terminate Active Administrative Session?",
      icon: <ExclamationCircleOutlined style={{ color: "#ef4444", fontSize: 22 }} />,
      content: (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
            All active authentication bearer tokens, operational switch keys, and session audit
            contexts will be securely flushed from this browser.
          </Text>
        </div>
      ),
      okText: "Terminate & Sign Out",
      okType: "danger",
      cancelText: "Stay Logged In",
      okButtonProps: {
        style: {
          borderRadius: 8,
          fontWeight: 600,
          background: "#dc2626",
          borderColor: "#dc2626",
          height: 38,
        },
      },
      cancelButtonProps: {
        style: { borderRadius: 8, height: 38 },
      },
      onOk: async () => {
        try {
          const token = localStorage.getItem("token");
          if (token) {
            await axios.get("https://billpay.mtmpay.in/Admin/api/admin/logout", {
              headers: { Authorization: `Bearer ${token}` },
            });
          }
        } catch (error) {
          console.warn("Server session cleanup notice:", error);
        } finally {
          localStorage.removeItem("token");
          sessionStorage.clear();
          dispatch(logout());
          message.success("Session closed. Returning to security portal.");
          navigate("/login");
        }
      },
    });
  };

  // Navigate to Profile
  const handleGoToProfile = () => {
    if (location.pathname.toLowerCase() === "/profile") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/Profile");
    }
  };

  // Clean, focused enterprise dropdown
  const profileMenuItems = {
    items: [
      {
        key: "header-meta",
        disabled: true,
        label: (
          <div className="dropdown-user-header">
            <span className="user-name-title">MTMPAY Central Ops</span>
            <span className="user-email-subtitle">info@mtmpay.in</span>
          </div>
        ),
      },
      { type: "divider" },
      {
        key: "profile",
        icon: <UserOutlined style={{ color: "#0284c7" }} />,
        label: "Administrator Profile",
        onClick: handleGoToProfile,
      },
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined style={{ color: "#ef4444" }} />,
        danger: true,
        label: "Sign Out",
        onClick: handleLogout,
      },
    ],
  };

  return (
    <Header className="enterprise-app-header">
      {/* Left: Switch Status & Realtime Sync Telemetry */}
      <div className="header-telemetry-cluster">
        <div className="bbps-switch-pill">
          <span className="pulse-dot" />
          <span className="switch-text">BBPS Switch: Operational</span>
        </div>

        <div className="header-metric-badge">
          <ThunderboltFilled style={{ color: "#0284c7", fontSize: 13 }} />
          <span>Latency: 28ms</span>
        </div>

        <div className="header-clock-badge">
          <ClockCircleOutlined style={{ color: "#64748b", fontSize: 13 }} />
          <span>
            {currentTime.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            })}
          </span>
        </div>
      </div>

      {/* Right: Executive Account Pill with Clean Dropdown */}
      <div className="header-actions-cluster">
        <Dropdown menu={profileMenuItems} trigger={["click"]} placement="bottomRight">
          <div className="admin-profile-pill">
            <Avatar size={34} className="header-avatar" icon={<UserOutlined />}>
              M
            </Avatar>
            <div className="admin-info-text">
              <span className="admin-primary-name">MTMPAY</span>
              <span className="admin-role-badge">Super Admin</span>
            </div>
            <DownOutlined className="dropdown-chevron" />
          </div>
        </Dropdown>
      </div>
    </Header>
  );
};

export default AppHeader;